import { FC } from 'react';
import { Col, Progress, Row } from 'antd';
import { TbArrowRight } from 'react-icons/tb';
import { Link, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import information from '@/manifest.json';
import { useUserStore } from '@/stores';
import { dayjs } from '@/utilities';

import { SmallTooltip } from '../../tooltips';

interface FreeTrialProgressProps {
    placement?:
        | 'bottom'
        | 'top'
        | 'left'
        | 'right'
        | 'topLeft'
        | 'topRight'
        | 'bottomLeft'
        | 'bottomRight'
        | 'leftTop'
        | 'leftBottom'
        | 'rightTop'
        | 'rightBottom'
        | undefined;
    margin?: any;
}

const FreeTrialProgress: FC<FreeTrialProgressProps> = ({ placement, margin }): JSX.Element => {
    const { token } = useAntDesignTheme();

    const navigate = useNavigate();

    const { selectedUser: user } = useUserStore();

    const trialLength = information.freeTrialDayLength ?? 14;

    const createdAt = user?.createdAt ? dayjs(user.createdAt) : null;

    const daysElapsed = createdAt ? dayjs().diff(createdAt, 'day') : 0;

    const remainingDaysRaw = trialLength - daysElapsed;
    const remainingDays = Math.max(0, remainingDaysRaw);

    const percentRemaining = Math.max(0, Math.min(100, (remainingDays / trialLength) * 100));

    const isExpired = remainingDaysRaw <= 0;
    const isUrgent = remainingDays <= 3 && !isExpired;

    const progressColor = isExpired ? token.colorError : isUrgent ? token.colorWarning : token.blue5;

    return (
        <SmallTooltip
            title={
                isExpired ? (
                    'Your free trial has ended. Upgrade to continue using premium features.'
                ) : (
                    <>
                        {remainingDays} day{remainingDays === 1 ? '' : 's'} remaining in your free trial.{' '}
                        <Link to={'/settings/account/plans/seats'}>
                            Upgrade your Plan <TbArrowRight style={{ verticalAlign: '-0.1rem' }} />
                        </Link>{' '}
                        to continue using Countercyclical past your trial period.
                    </>
                )
            }
            placement={placement}
            margin={margin}
        >
            <Row
                wrap={false}
                align='middle'
                style={{
                    cursor: 'help',
                    padding: '0 0.375rem',
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    backgroundColor: token.colorBgBase,
                }}
                onClick={() => navigate('/settings/account/plans/seats')}
            >
                <Col flex='none' style={{ paddingRight: '0.5rem' }}>
                    <Progress
                        type='circle'
                        percent={percentRemaining}
                        trailColor={token.colorBorder}
                        strokeColor={progressColor}
                        showInfo={false}
                        width={14}
                        strokeWidth={16}
                        style={{ marginBottom: '0.125rem' }}
                    />
                </Col>
                <Col flex='none'>
                    <small
                        style={{
                            verticalAlign: '1px',
                            color: isExpired
                                ? token.colorError
                                : isUrgent
                                  ? token.colorWarning
                                  : token.colorTextSecondary,
                            fontWeight: isUrgent || isExpired ? 500 : 400,
                        }}
                    >
                        {isExpired ? 'Trial Expired' : `${remainingDays} Day${remainingDays === 1 ? '' : 's'} Left`}
                    </small>
                </Col>
            </Row>
        </SmallTooltip>
    );
};

export default FreeTrialProgress;
